/**
 * useWlBillingGateways — hooks de suporte ao bloco "Integração" do painel WL.
 *
 * Expõe duas consultas:
 *   - useWlBillingGateways(): gateways de pagamento ativos (popula o select do form).
 *   - useWlMasterRecurringServices(search): assinaturas ativas do tenant master
 *     (popula o select assíncrono quando o gateway escolhido é "uaizy").
 *
 * Ambas chamam RPCs SECURITY DEFINER (wl_list_billing_gateways e
 * wl_list_master_recurring_services). Cache curto + chave por termo de busca
 * para evitar flood ao digitar.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlBillingGateways");

export interface WlBillingGateway {
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_implemented: boolean;
  sort_order: number;
}

export function useWlBillingGateways(includeInactive = false) {
  return useQuery({
    queryKey: ["wl-billing-gateways", { includeInactive }],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<WlBillingGateway[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_billing_gateways" as never,
        { p_include_inactive: includeInactive } as never,
      );
      if (error) {
        log.error("Erro ao listar billing_gateways", { error });
        throw error;
      }
      return (data || []) as WlBillingGateway[];
    },
  });
}

export interface WlMasterRecurringService {
  id: string;
  descricao: string | null;
  valor_total: number;
  moeda: string | null;
  frequencia: string | null;
  status: string | null;
  proximo_vencimento: string | null;
  created_at: string;
}

export function useWlMasterRecurringServices(search: string | null, enabled = true) {
  const term = (search || "").trim() || null;
  return useQuery({
    queryKey: ["wl-master-recurring-services", { term }],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<WlMasterRecurringService[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_master_recurring_services" as never,
        { p_search: term } as never,
      );
      if (error) {
        log.error("Erro ao listar serviços recorrentes do master", { error });
        throw error;
      }
      return ((data || []) as any[]).map((s) => ({
        ...s,
        valor_total: Number(s.valor_total) || 0,
      })) as WlMasterRecurringService[];
    },
  });
}
